import { useSyncExternalStore } from 'react';

/**
 * Whether this browser can install the app, and asking it to.
 *
 * ## Why the event is caught at start-up, not on the settings page
 *
 * Chrome fires `beforeinstallprompt` **once per page load**, as soon as it has
 * decided the site is installable — usually before anybody has navigated to
 * Indstillinger → Mobil-app. A listener attached when that page mounts would
 * miss it and the page would only ever show the manual instructions. So
 * `main.tsx` calls `installPrompt.listen(window)` before React renders, and the
 * page reads what was caught.
 *
 * ## Why `preventDefault()`
 *
 * It suppresses Chrome's own install mini-infobar. Without it, every visit to
 * the full bookkeeping UI on a phone would offer to install — and what gets
 * installed is the two-button capture app, not the screen the offer appeared
 * over. The offer belongs on the page that explains what is being installed.
 * Chrome's menu entry ("Installér app") is unaffected.
 *
 * ## Three states, not two
 *
 * - **installed** — running as the installed app, or Chrome reported
 *   `appinstalled` during this page load. A browser tab cannot tell whether the
 *   app is installed *elsewhere*, and this does not pretend to.
 * - **available** — Chrome handed over a prompt; one button installs.
 * - **manual** — no prompt: Safari and Firefox never fire one, Chrome withholds
 *   it on desktop without engagement, and the event is single-use. The page then
 *   says how to install from the browser's own menu.
 */

/** Chrome's event. Not in lib.dom, because it never left incubation. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

export type InstallState = 'installed' | 'available' | 'manual';

export type PromptOutcome = 'accepted' | 'dismissed' | 'unavailable';

export class InstallPrompt {
  #deferred: BeforeInstallPromptEvent | null = null;
  #installed = false;
  readonly #listeners = new Set<() => void>();
  readonly #isStandalone: () => boolean;

  constructor(isStandalone: () => boolean) {
    this.#isStandalone = isStandalone;
  }

  /** Start catching the browser's events. Returns a function that stops. */
  listen(target: EventTarget): () => void {
    const onPrompt = (event: Event): void => {
      event.preventDefault();
      this.#deferred = event as BeforeInstallPromptEvent;
      this.#notify();
    };
    const onInstalled = (): void => {
      this.#deferred = null;
      this.#installed = true;
      this.#notify();
    };
    target.addEventListener('beforeinstallprompt', onPrompt);
    target.addEventListener('appinstalled', onInstalled);
    return () => {
      target.removeEventListener('beforeinstallprompt', onPrompt);
      target.removeEventListener('appinstalled', onInstalled);
    };
  }

  state(): InstallState {
    if (this.#installed || this.#isStandalone()) {
      return 'installed';
    }
    return this.#deferred === null ? 'manual' : 'available';
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Show the browser's install dialog.
   *
   * The event can be used exactly once — a second `prompt()` on it rejects — so
   * it is released before the dialog opens. If the person dismisses it, the
   * page falls back to the manual instructions, which is the truth: Chrome
   * will not offer the button again until a later page load.
   */
  async prompt(): Promise<PromptOutcome> {
    const event = this.#deferred;
    if (event === null) {
      return 'unavailable';
    }
    this.#deferred = null;
    this.#notify();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  }

  #notify(): void {
    for (const listener of this.#listeners) {
      listener();
    }
  }
}

/** The one instance the application uses. See the module comment for why it is module state. */
export const installPrompt = new InstallPrompt(
  () => window.matchMedia('(display-mode: standalone)').matches,
);

// Module-level, so `useSyncExternalStore` sees the same functions on every
// render and does not resubscribe each time.
const subscribe = (listener: () => void): (() => void) => installPrompt.subscribe(listener);
const snapshot = (): InstallState => installPrompt.state();

export function useInstallState(): InstallState {
  return useSyncExternalStore(subscribe, snapshot);
}
